import { api, SKIP_AUTO_CONTENT_TYPE_HEADER } from './api';

// Define an Assistant service
export const assistantApi = api.injectEndpoints({
  reducerPath: 'assistantApi',
  tagTypes: ['Assistants', 'UserAssistants', 'UserAssistant'],
  endpoints: (builder) => ({
    getAssistants: builder.query({
      query: () => {
        return {
          url: `/api/administration/assistants`,
          method: 'GET'
        };
      },
      providesTags: ['Assistants']
    }),
    getChatAssistants: builder.query({
      query: (params = {}) => {
        const { isPublic = false } = params;
        return {
          url: !isPublic ? `/api/assistants` : `/public/api/assistants`,
          method: 'GET'
        };
      },
      providesTags: ['ChatAssistants']
    }),
    getAssistant: builder.query({
      query: ({ assistantId }) => ({
        url: `/api/administration/assistants/${assistantId}`,
        method: 'GET'
      }),
      providesTags: ['Assistant']
    }),
    getChatAssistant: builder.query({
      query: ({ assistantCode, isPublic = false }) => ({
        url: !isPublic
          ? `/api/assistant/${assistantCode}`
          : `/public/api/assistant/${assistantCode}`,
        method: 'GET'
      }),
      providesTags: ['ChatAssistant']
    }),
    getAssistantTools: builder.query({
      query: ({ assistantId }) => {
        return {
          url: `/api/administration/assistants/${assistantId}/tools`,
          method: 'GET'
        };
      },
      keepUnusedDataFor: 0
    }),
    createAssistant: builder.mutation({
      query: (assistantRequest) => ({
        url: `/api/administration/assistants`,
        method: 'POST',
        body: assistantRequest
      }),
      invalidatesTags: ['Assistants', 'Assistant', 'ChatAssistant', 'ChatAssistants']
    }),
    updateAssistant: builder.mutation({
      query: ({ assistantId, assistantRequest }) => ({
        url: `/api/administration/assistants/${assistantId}`,
        method: 'PATCH',
        body: assistantRequest
      }),
      invalidatesTags: ['Assistants', 'Assistant', 'ChatAssistant', 'ChatAssistants']
    }),
    updateAssistantFlow: builder.mutation({
      query: ({ assistantId, flow }) => ({
        url: `/api/administration/assistants/${assistantId}/flow`,
        method: 'PATCH',
        body: flow
      }),
      invalidatesTags: ['Assistants', 'Assistant', 'ChatAssistant', 'ChatAssistants']
    }),
    deleteAssistant: builder.mutation({
      query: (assistantId) => ({
        url: `/api/administration/assistants/${assistantId}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['Assistants', 'Assistant', 'ChatAssistant', 'ChatAssistants']
    }),
    bulkDeleteAssistants: builder.mutation({
      query: (assistantIds) => ({
        url: `/api/administration/assistants`,
        method: 'DELETE',
        body: assistantIds
      }),
      invalidatesTags: ['Assistants', 'Assistant', 'ChatAssistant', 'ChatAssistants']
    }),
    generateStream: builder.mutation({
      query: ({ files, request, onChunk, signal, isPublic = false }) => {
        const formData = new FormData();

        if (files && files.length > 0) {
          files.forEach((file) => {
            formData.append('files', file);
          });
        }

        // Ajouter le request JSON
        formData.append(
          'request',
          new Blob([JSON.stringify(request)], { type: 'application/json' })
        );

        return {
          url: !isPublic
            ? `/api/assistant/stream/generate`
            : `/public/api/assistant/stream/generate`,
          method: 'POST',
          body: formData,
          headers: {
            Accept: 'text/event-stream',
            [SKIP_AUTO_CONTENT_TYPE_HEADER]: 'true'
          },
          credentials: 'include',
          signal,
          responseHandler: (response) => {
            if (!response.ok) {
              throw new Error(`API call failed : ${response.statusText}`);
            }

            const stream = response.body.getReader();
            const matcher = /\r?\n/;
            const decoder = new TextDecoder();
            let buf = '';

            return stream.read().then(function processText({ done, value }) {
              if (done) {
                if (buf.length > 0) onChunk(JSON.parse(buf)?.content);
                return null;
              }
              const chunk = decoder.decode(value, {
                stream: true
              });
              buf += chunk.replaceAll('data:', '');

              const parts = buf.split(matcher);
              buf = parts.pop();
              for (const i of parts.filter((p) => p)) onChunk(JSON.parse(i)?.content);
              return stream.read().then(processText);
            });
          }
        };
      }
    }),
    generate: builder.mutation({
      query: ({ file, request }) => {
        const formData = new FormData();

        if (file) {
          formData.append('file', file);
        }

        // Ajouter le request JSON
        formData.append(
          'request',
          new Blob([JSON.stringify(request)], { type: 'application/json' })
        );

        return {
          url: `/api/assistant/generate`,
          method: 'POST',
          body: formData,
          credentials: 'include',
          responseHandler: (response) => response.text(),
          headers: {
            [SKIP_AUTO_CONTENT_TYPE_HEADER]: 'true'
          }
        };
      }
    })
  })
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetAssistantsQuery,
  useGetChatAssistantsQuery,
  useGetAssistantQuery,
  useGetChatAssistantQuery,
  useCreateAssistantMutation,
  useUpdateAssistantMutation,
  useUpdateAssistantFlowMutation,
  useDeleteAssistantMutation,
  useBulkDeleteAssistantsMutation,
  useGenerateStreamMutation,
  useGenerateMutation,
  useGetAssistantToolsQuery
} = assistantApi;
